import { Button, CircularProgress } from "@mui/material";
import { MdSave } from "react-icons/md";
import ViewClientProfile from "components/Clients/ViewClientProfile";
import AddAccountSecondTable from "../AddAccountSecondTable";
import { AutoSaveStatus } from "./AccountEditorHeader";
import {
  sumAdmissionInstallmentTotals,
  countedBonus,
  countedCommission,
  isAdmissionTermReached,
  toNumber,
} from "../accountUtils";
import { RefreshData } from "components/helper";
import { IoArrowBack } from "react-icons/io5";
import {
  AccountMetricTile,
  absMoney,
  dueLabel,
  dueTone,
  effectiveDueHint,
  money,
  profitLabel,
  profitTone,
  termCountLabel,
} from "./accountEditorUi";

/**
 * Full-page admission installment editor — separate from the account tabs shell.
 * Accumulated installment totals sit above the term table.
 */
const AccountAdmissionDetailPage = ({
  row,
  admissionValues = {},
  clientId,
  clientName,
  onBack,
  onRefresh,
  onOpenEye,
  onViewAdmission,
  onViewFeeDetails,
  onGenerateInvoice,
  onToggleNotes,
  accountDetails,
  setAccountDetails,
  clientData,
  onDirty,
  autoOpenVerification,
  verificationFocusUuid,
  onVerificationOpened,
  autoCloseVerificationWhenEmpty = false,
  termNotesFocus,
  onTermNotesOpened,
  onVerifySuccess,
  autoSaveStatus,
  onSave,
  isSaving = false,
  onAdjustSettlement,
  /**
   * The term schedule merged onto the installments, so each row carries its term's withdrawal
   * columns. The table and the totals take every term, imported or not, like the server; the
   * effective due keeps to reached ones.
   */
  totalsInstallments,
}) => {
  const installments = totalsInstallments?.length
    ? totalsInstallments
    : row?.admissionDetails || [];
  const importedCount = installments.filter((item) => !item._unimported).length;
  const totals = sumAdmissionInstallmentTotals(installments);
  // Counted, not scheduled: a withdrawal writes off whatever it did not retain.
  const commission = installments.reduce((sum, item) => sum + countedCommission(item), 0);
  const bonus = installments.reduce((sum, item) => sum + countedBonus(item), 0);
  const hasFutureTerms = installments.some((item) => !isAdmissionTermReached(item));
  const due = toNumber(totals.dueAmount);
  const effectiveDue = toNumber(totals.effectiveDueAmount);
  const profit = toNumber(totals.profitLoss);
  const rowUuid = row?.uuid;

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-2 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text">
          <div className="px-4 py-3 border-b border-app-border">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={onBack}
                  className="mt-1 inline-flex items-center gap-1 text-lg font-medium text-primary bg-transparent border-0 p-0 cursor-pointer hover:underline shrink-0"
                >
                  <IoArrowBack />
                </button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-lg font-bold text-app-text m-0 truncate">
                      {admissionValues.course || "Admission"}
                    </h1>
                    {onRefresh && <RefreshData refreshData={onRefresh} />}
                  </div>
                  <p className="text-xs text-app-text-muted m-0 mt-0.5 truncate">
                    {[
                      admissionValues.university,
                      admissionValues.intake,
                      row?.admissionStatus || admissionValues.status,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Installments"}
                    {clientId ? (
                      <>
                        {" · "}
                        <ViewClientProfile
                          clientId={clientId}
                          clientName={clientName}
                          variant="link"
                          displayName={clientName || "Client"}
                        />
                      </>
                    ) : null}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Terms autosave, but a table of editable amounts gives no sign of it — say where
                    the edits stand, and let someone commit them now instead of waiting. */}
                <AutoSaveStatus status={autoSaveStatus} />
                <Button size="small" variant="outlined" onClick={() => onOpenEye?.(row)}>
                  Course details
                </Button>
                <Button size="small" variant="outlined" onClick={() => onViewAdmission?.(row)}>
                  View Admission
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onViewFeeDetails?.(row)}
                  target="_blank"
                >
                  Fee Details
                </Button>
                {onGenerateInvoice && (
                  <Button size="small" variant="outlined" onClick={() => onGenerateInvoice(row)}>
                    Invoice
                  </Button>
                )}
                {/* {onToggleNotes && (
                  <Button size="small" variant="text" onClick={() => onToggleNotes(row)}>
                    Notes
                  </Button>
                )} */}
                {onSave && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onSave}
                    disabled={isSaving}
                    startIcon={
                      isSaving ? <CircularProgress size={14} sx={{ color: "white" }} /> : <MdSave />
                    }
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="px-4 py-4 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-app-text-muted m-0">
                  Term totals
                </p>
                <span className="text-xs text-app-text-muted">
                  {termCountLabel({
                    imported: importedCount,
                    notImported: installments.length - importedCount,
                  })}
                </span>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted m-0 mb-1.5">
                  Client
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <AccountMetricTile label="Agreed" value={money(totals.agreedAmount)} emphasize />
                  <AccountMetricTile label="Paid" value={money(totals.paidAmount)} emphasize />
                  <AccountMetricTile
                    label={dueLabel(due)}
                    value={absMoney(due)}
                    tone={dueTone(due)}
                    emphasize
                    hint={effectiveDueHint(due, effectiveDue)}
                  />
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted m-0 mb-1.5">
                  Agency
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <AccountMetricTile label="Agent cost" value={money(totals.cost)} emphasize />
                  <AccountMetricTile label="Discount" value={money(totals.discount)} emphasize />
                  <AccountMetricTile label="Commission" value={money(commission)} emphasize />
                  <AccountMetricTile label="Bonus" value={money(bonus)} emphasize />
                  <AccountMetricTile
                    label={profitLabel(profit)}
                    value={absMoney(profit)}
                    tone={profitTone(profit)}
                    emphasize
                    hint={hasFutureTerms ? "Terms reached by today only" : undefined}
                  />
                </div>
              </div>
            </div>

            <AddAccountSecondTable
              summaryMode={false}
              parentId={row.uuid}
              accountDetails={accountDetails}
              setAccountDetails={setAccountDetails}
              data={installments}
              clientData={clientData}
              onDirty={onDirty}
              onLocalNoteAdded={onDirty}
              clientAccountDetailId={row.id}
              openVerificationOnMount={autoOpenVerification && verificationFocusUuid === rowUuid}
              autoCloseVerificationWhenEmpty={autoCloseVerificationWhenEmpty}
              onVerificationModalOpened={onVerificationOpened}
              openTermNotesOnMount={
                termNotesFocus && termNotesFocus.uuid === rowUuid ? termNotesFocus.termNumber : null
              }
              onTermNotesOpened={onTermNotesOpened}
              onVerifySuccess={onVerifySuccess}
              onAdjustSettlement={onAdjustSettlement ? () => onAdjustSettlement(row) : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountAdmissionDetailPage;
